import { Box, Button, Chip, MenuItem, Stack, Typography } from "@mui/material";
import { omit } from "lodash-es";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import ListViewLayout from "@/App/Layouts/ListViewLayout";
import SimpleViewPageHeader from "@/App/Layouts/PageHeader/SimpleViewPageHeader";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataExportModal from "@/common/components/DataExport/DataExportModal";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import DateRangeDuration from "@/common/components/Datetime/DateRangeDuration";
import Datetime from "@/common/components/Datetime/Datetime";
import InlineUser from "@/common/components/Entity/User/InlineUser";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import TimeSpanDisplay from "@/common/components/Form/Display/TimeSpanDisplay";
import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import TableCellContentOfAffiliation from "@/common/components/Table/TableCell/TableCellContentOfAffiliation";
import TableCellContentOfAffiliationInDepartments from "@/common/components/Table/TableCell/TableCellContentOfAffiliationInDepartments";
import TableCellContentOfAffiliationInLocations from "@/common/components/Table/TableCell/TableCellContentOfAffiliationInLocations";
import TableCellContentOfAttachments from "@/common/components/Table/TableCell/TableCellContentOfAttachments";
import TableCellContentOfTags from "@/common/components/Table/TableCell/TableCellContentOfTags";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ROUTE_PATH } from "@/common/constants/routing";
import { FilterCatalog } from "@/common/filters/filterCatalog";
import { PermissionHelper } from "@/common/helpers/authorization/permission";
import { EntityHelper } from "@/common/helpers/entity";
import { TagStaticFilterInputDto, TagsHelper } from "@/common/helpers/entity/tags";
import { TabularHelper } from "@/common/helpers/tabular";
import { getTypedPath } from "@/common/helpers/typedPath";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCommonRequestParams } from "@/common/hooks/api/useCommonRequestParams";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { PropagatedDeps } from "@/common/hooks/render/usePropagatedDeps";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { FilterFieldType } from "@/common/ts/filters";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  ContractDto,
  ContractFilterType,
  ContractGetPaginatedDto,
  DataUpdatesHubClientMethodName,
  EntityType,
  ExportType,
  FilterDefinitionDto,
  GeneralAttachedTagsInputDto,
  TagEntityType,
  UnitOfTime,
  VisualInspectionDto,
} from "@/core/api/generated/v1";

import AssetSubscriptionInline from "../../AssetSubscription/AssetSubscriptionInline";
import CustomerInline from "../../Customer/CustomerInline";
import TenantStructureMembershipOfEntityEditModal from "../../EntityAffiliation/BulkUpdate/TenantStructureMembershipOfEntityEditModal";
import GeneralAttachedTagsOfEntitiesEditModal from "../../General/GeneralTag/GeneralAttachedTagsOfEntitiesEditModal";
import VehicleInline from "../../Vehicle/VehicleInline";
import ContractMenu from "../ContractMenu";
import ContractsDeleteModal from "../ContractsDeleteModal";

const defaultDisplayProps = {
  header: true,
  filters: true,
  counters: true,
  create: true,
  export: true,
  edit: true,
  delete: true,
  viewVariant: ViewVariant.Page,
};

enum BulkActionFlags {
  UpdateAffiliation = "UpdateAffiliation",
  UpdateTags = "UpdateTags",
  Export = "Export",
  Delete = "Delete",
}
const defaultPropagatedDeps = {
  refetch: {},
};

export type ContractPaginatedListPropagatedDepsMap = typeof defaultPropagatedDeps;

export interface ContractPaginatedListOwnProps {
  displayProps?: Partial<typeof defaultDisplayProps>;
  initialValues?: {
    filterDefinitionDto?: Nil<FilterDefinitionDto>;
    isResetFilterDefinition?: boolean;
    filterType?: ContractFilterType;
  };
  defaultValues?: {
    limit?: number;
    vehicleId?: string;
    customerId?: string;
  };
  propagatedDeps?: PropagatedDeps<ContractPaginatedListPropagatedDepsMap>;
}

export type ContractPaginatedListProps = ContractPaginatedListOwnProps;

type ContractGetPaginatedInputDto = Omit<ContractGetPaginatedDto, "tags"> & {
  tags?: TagStaticFilterInputDto;
};
export default function ContractPaginatedList({
  displayProps,
  initialValues,
  defaultValues,
  propagatedDeps,
}: ContractPaginatedListProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();
  const commonRequestParams = useCommonRequestParams<ContractGetPaginatedInputDto>({
    statePersistence: {
      persistenceKey: EntityType.Contract,
    },
    initialValues: {
      filterDefinitionDto: initialValues?.filterDefinitionDto || undefined,
      isResetFilterDefinition: initialValues?.isResetFilterDefinition,
      params: {
        ...(initialValues?.filterType ? { filterType: initialValues?.filterType } : undefined),
      },
    },
    defaultValues: {
      limit: defaultValues?.limit,
      params: {
        filterType: ContractFilterType.All,
        ...defaultValues,
      },
    },
    initParams: (filterDefinition) => {
      const tagFilter = filterDefinition.filterDefinition?.items.find(
        (x) => x.field === getTypedPath<ContractDto>().tags.$path,
      );

      return {
        tags: {
          operator: tagFilter?.operator,
          query: tagFilter?.value as GeneralAttachedTagsInputDto,
        } as TagStaticFilterInputDto,
      };
    },
    options: { useUrlSearchParamsToStoreSelectedPage: true },
  });

  const [isDataExportModalOpened, setIsDataExportModalOpened] = useState(false);

  const isCountersVisible = displayProps?.counters && !commonRequestParams.isAnyFilters;

  const countersRequest = useApiRequest(
    apiClient.contractsApi.apiV1ContractsCountersGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      contractGetCountersDto: {},
    },
    {
      deps: [propagatedDeps?.depsMap["refetch"]],
    },
  );
  const counters = countersRequest?.data;

  const paginatedContractsRequest = useApiRequest(
    apiClient.contractsApi.apiV1ContractsGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      contractGetPaginatedDto: {
        ...omit(commonRequestParams.params, "tags"),
        offset: commonRequestParams.offset,
        limit: commonRequestParams.limit,
        search: commonRequestParams.search,
        tags:
          commonRequestParams.filterDefinition?.items
            .filter((x) => x.field === getTypedPath<ContractDto>().tags.$path)
            .map((x) => TagsHelper.mapFromFilter(x))[0] || undefined,
        sortDefinition: commonRequestParams.sortDefinitionDto,
        filterDefinition: commonRequestParams.filterDefinitionDto,
      },
    },
    {
      deps: [
        ...commonRequestParams.deps,
        commonRequestParams.filterDefinition,
        propagatedDeps?.depsMap["refetch"],
      ],
      debouncedDeps: {
        deps: [...commonRequestParams.debouncedDeps],
        wait: 500,
        options: { leading: false, trailing: true },
      },
      commonRequestParams: commonRequestParams,
    },
  );
  const paginatedContracts = useMemo(
    () => paginatedContractsRequest?.data,
    [paginatedContractsRequest.data],
  );
  const contractIds = useMemo(
    () => paginatedContracts?.items?.map((x) => x.id!) || [],
    [paginatedContracts],
  );

  useRealtimeDataUpdates({
    channelNames: [DataUpdatesChannelName.Entities(currentTenant?.id, EntityType.Contract)],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      paginatedContractsRequest.handleEntityChanged(data);
      countersRequest.refetch();
    },
  });

  return (
    <ListViewLayout
      displayProps={displayProps}
      header={
        displayProps?.header && (
          <SimpleViewPageHeader
            title='Contracts'
            primaryActions={
              displayProps?.create && (
                <AuthorizedElement permissions={[AppPermission.ContractManage]}>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={<AppIcon of='add' />}
                    component={RouterLink}
                    to={ROUTE_PATH.CONTRACT_CREATE()}
                  >
                    Create new contract
                  </Button>
                </AuthorizedElement>
              )
            }
            secondaryActions={
              displayProps?.export && (
                <AuthorizedElement permissions={[AppPermission.ExportManage]}>
                  <MenuWithTrigger
                    withAuthCloseOnClick
                    trigger={
                      <AppIconButton>
                        <AppIcon of='moreVert' />
                      </AppIconButton>
                    }
                  >
                    <MenuItem onClick={() => setIsDataExportModalOpened(true)}>
                      <AppIcon of='export' fontSize='small' sx={{ mr: 1 }} />
                      <Typography>Export contracts</Typography>
                    </MenuItem>
                  </MenuWithTrigger>
                </AuthorizedElement>
              )
            }
          />
        )
      }
    >
      <DataTabular<ContractDto>
        tabs={{
          default: {
            tabsProps: {
              value: commonRequestParams.params?.filterType,
              onChange: (e, newValue) => {
                commonRequestParams.setPaginationInfo({
                  limit: commonRequestParams.paginationInfo?.limit,
                  offset: 0,
                });
                commonRequestParams.setOneParam2({ filterType: newValue });
              },
            },
            tabPropsList: enumService
              .getEnumValues("ContractFilterType", {
                except: [ContractFilterType.None],
              })
              .map((value) => ({
                label: (
                  <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                    <Box>
                      <InlineApiEnumValue
                        nameSeparator=''
                        type='ContractFilterType'
                        value={value}
                      />
                    </Box>
                    {isCountersVisible && counters?.countByFilterTypeMap && (
                      <Chip
                        variant='outlined'
                        size='small'
                        color='default'
                        label={counters.countByFilterTypeMap[value] ?? 0}
                      />
                    )}
                  </Stack>
                ),
                value,
              })),
          },
        }}
        columns={[
          TabularHelper.getColumnForEntityChips<ContractDto>(),
          {
            field: getTypedPath<ContractDto>().id.$path,
            title: "ID",
            width: 250,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.id,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().localNumber.$path,
            title: "Number",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <Stack direction='column' alignItems='flex-start'>
                {item.localNumber}
                {item.externalNumber && (
                  <AppTooltip variant='helpText' title='External number'>
                    <AppTypography ellipsing={{ enabled: true }} component='div' variant='body2'>
                      ({item.externalNumber})
                    </AppTypography>
                  </AppTooltip>
                )}
              </Stack>
            ),
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().externalNumber.$path,
            title: "Ext. number",
            description: "External number",
            width: 100,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.externalNumber,
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().stageHistory.stage.$path,
            title: "Stage",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <InlineApiEnumValue type='ContractStage' value={item.stageHistory!.stage} />
            ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.ContractStage,
                },
              },
            },
          },
          {
            field: getTypedPath<ContractDto>().filterType.$path,
            title: "Filter type",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <InlineApiEnumValue type='ContractFilterType' value={item.filterType} />
            ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.ContractFilterType,
                },
              },
            },
          },
          {
            field: getTypedPath<ContractDto>().vehicle.id.$path,
            title: "Vehicle",
            width: 300,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => <VehicleInline entity={item.vehicle} />,
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfVehicle(),
            },
          },
          {
            field: getTypedPath<ContractDto>().customer.id.$path,
            title: "Customer",
            width: 300,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => <CustomerInline entity={item.customer} />,
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfCustomer(),
            },
          },
          {
            field: getTypedPath<ContractDto>().assetSubscription.id.$path,
            title: "Asset subscription",
            width: 300,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.assetSubscription ? (
                <AssetSubscriptionInline entity={item.assetSubscription} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfAssetSubscription(),
            },
          },
          {
            field: getTypedPath<ContractDto>().startsAt.$path,
            title: "Starts at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.startsAt ? (
                <Datetime datetime={item.startsAt} withDurationFromNow={false} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().startsAtEffective.$path,
            title: "Effective start at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.startsAtEffective ? (
                <Datetime datetime={item.startsAtEffective} withDurationFromNow={false} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().endsAt.$path,
            title: "Ends at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.endsAt ? <Datetime datetime={item.endsAt} withDurationFromNow={false} /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().endsAtEffective.$path,
            title: "Effective end at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.endsAtEffective ? (
                <Datetime datetime={item.endsAtEffective} withDurationFromNow={false} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().bufferBefore.$path,
            title: "Buffer before",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.bufferBefore ? <TimeSpanDisplay value={item.bufferBefore} /> : "-",
          },
          {
            field: getTypedPath<ContractDto>().bufferAfter.$path,
            title: "Buffer after",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.bufferAfter ? <TimeSpanDisplay value={item.bufferAfter} /> : "-",
          },
          {
            field: "duration",
            title: "Duration",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            renderCell: (item) => (
              <DateRangeDuration
                range={{
                  startsAt: item.startsAt,
                  endsAt: item.endsAt,
                }}
                humanizeOptions={{
                  maxUnit: UnitOfTime.Day,
                }}
              />
            ),
          },
          {
            field: "durationEffective",
            title: "Effective duration",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            renderCell: (item) => (
              <DateRangeDuration
                range={{
                  startsAt: item.startsAtEffective,
                  endsAt: item.endsAtEffective,
                }}
                humanizeOptions={{
                  maxUnit: UnitOfTime.Day,
                }}
              />
            ),
          },
          {
            field: getTypedPath<ContractDto>().reservationExpiresAt.$path,
            title: "Reservation expires at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.reservationExpiresAt ? (
                <Datetime datetime={item.reservationExpiresAt} withDurationFromNow={false} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().type.$path,
            title: "Type",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => <InlineApiEnumValue type='ContractType' value={item.type} />,
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.ContractType,
                },
              },
            },
          },
          {
            field: getTypedPath<ContractDto>().allocationStatus.$path,
            title: "Allocation status",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <InlineApiEnumValue type='AllocationStatus' value={item.allocationStatus} />
            ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.AllocationStatus,
                },
              },
            },
          },
          {
            field: getTypedPath<ContractDto>().notes.$path,
            title: "Notes",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => item.notes || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().createdAt.$path,
            title: "Created at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.createdAt ? <Datetime datetime={item.createdAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().updatedAt.$path,
            title: "Updated at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedAt ? <Datetime datetime={item.updatedAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<ContractDto>().createdBy.$path,
            title: "Created by",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.createdBy ? <InlineUser userId={item.createdBy} withAvatar /> : "-",
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
            },
          },
          {
            field: getTypedPath<ContractDto>().updatedBy.$path,
            title: "Updated by",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedBy ? <InlineUser userId={item.updatedBy} withAvatar /> : "-",
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
            },
          },
          {
            field: getTypedPath<VisualInspectionDto>().attachments.$path,
            title: "Attachments",
            width: 120,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            isToDisabled: true,
            renderCell: (item) => <TableCellContentOfAttachments attachments={item.attachments} />,
          },
          {
            field: "affiliation",
            title: "Affiliation",
            description: "Affiliation to departments & locations",
            width: 140,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: false,
            isToDisabled: true,
            renderCell: (item) => <TableCellContentOfAffiliation entity={item} />,
          },
          // TODO: check why this col is not visible
          {
            field: getTypedPath<ContractDto>().departmentIds.$path,
            title: "Departments",
            width: 120,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isToDisabled: false,
            renderCell: (item) => <TableCellContentOfAffiliationInDepartments entity={item} />,
            filters: {
              fieldType: FilterFieldType.ArrayOfId,
              operators: FilterCatalog.getOperatorsForIdFieldOfDepartment(),
            },
          },
          {
            field: getTypedPath<ContractDto>().locationIds.$path,
            title: "Locations",
            width: 120,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isToDisabled: false,
            renderCell: (item) => <TableCellContentOfAffiliationInLocations entity={item} />,
            filters: {
              fieldType: FilterFieldType.ArrayOfId,
              operators: FilterCatalog.getOperatorsForIdFieldOfLocation(),
            },
          },
          {
            field: getTypedPath<ContractDto>().tags.$path,
            title: "Tags",
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isColumnMenuDisabled: false,
            isToDisabled: true,
            renderCell: (item) => (
              <TableCellContentOfTags
                entityType={TagEntityType.Contract}
                entity={item}
                onUpdate={(newValue) =>
                  paginatedContractsRequest.updateData((data) => {
                    data.items?.forEach((item2) => {
                      if (item2.id === item.id) {
                        item2.tags = newValue || undefined;
                      }
                    });
                  })
                }
              />
            ),
            filters: {
              fieldType: FilterFieldType.Arbitrary,
              isUseSingleFilter: true,
              operators: FilterCatalog.getOperatorsForTagsField(),
            },
          },
        ]}
        isLoading={paginatedContractsRequest.isLoading}
        rows={paginatedContracts?.items}
        getRowId={(item) => item.id!}
        rowTo={(item) => ROUTE_PATH.CONTRACT_VIEW(item.id)}
        renderRowAction={({ item }) => (
          <ContractMenu
            entity={item}
            onDelete={() => paginatedContractsRequest.refetch()}
            onUpdate={() => paginatedContractsRequest.refetch()}
            displayProps={{
              actions: {
                assessments: true,
                edit: displayProps?.edit || false,
                delete: displayProps?.delete || false,
                customerCommunication: false,
                contractRemainders: false,
                eventLog: false,
                //quick action
                createVisualInspection: false,
                createDamageDetection: false,
                createDamageCostEvaluation: false,
                createRepairOperation: false,
                createAccessoryCheck: false,
                createCharge: false,
              },
            }}
          />
        )}
        statePersistence={commonRequestParams.dataTabularProps.statePersistence}
        pagination={commonRequestParams.dataTabularProps.pagination}
        sort={commonRequestParams.dataTabularProps.sort}
        quickFilter={commonRequestParams.dataTabularProps.quickFilter}
        filters={commonRequestParams.dataTabularProps.filters}
        refetch={commonRequestParams.dataTabularProps.refetch}
        bulkActions={{
          enabled: true,
          definition: BulkActionFlags,
          actionTriggers: ({ currentAction, startAction, selectedIds }) => (
            <>
              <AuthorizedElement
                permissions={PermissionHelper.presets.entityAffiliationManage(
                  AppPermission.ContractManage,
                )}
              >
                <AppIconButton
                  tooltipProps={{ title: "Update affiliation" }}
                  onClick={() => startAction(BulkActionFlags.UpdateAffiliation)}
                >
                  <AppIcon of='department' />
                </AppIconButton>
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.ContractManage]}>
                <AppIconButton
                  tooltipProps={{ title: "Update tags" }}
                  onClick={() => startAction(BulkActionFlags.UpdateTags)}
                >
                  <AppIcon of='tag' />
                </AppIconButton>
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.ContractManage]}>
                <AppIconButton
                  tooltipProps={{ title: "Delete" }}
                  onClick={() => startAction(BulkActionFlags.Delete)}
                >
                  <AppIcon of='delete' />
                </AppIconButton>
              </AuthorizedElement>

              <AuthorizedElement permissions={[AppPermission.ExportManage]}>
                <AppIconButton
                  tooltipProps={{ title: "Export" }}
                  onClick={() => startAction(BulkActionFlags.Export)}
                >
                  <AppIcon of='export' />
                </AppIconButton>
              </AuthorizedElement>
            </>
          ),
          actionHandlers: ({ selectedIds, currentAction, cancelAction, completeAction }) => {
            const selectedEntities = EntityHelper.filterEntitiesByIds(
              paginatedContracts?.items || [],
              selectedIds as string[],
            );
            return (
              <>
                <TenantStructureMembershipOfEntityEditModal
                  open={currentAction === BulkActionFlags.UpdateAffiliation}
                  onClose={() => cancelAction()}
                  tenantStructureMembershipOfEntityEditProps={{
                    entityType: EntityType.Contract,
                    entities: selectedEntities,
                    onSave: () => {
                      completeAction();
                      paginatedContractsRequest.refetch();
                    },
                  }}
                />
                <GeneralAttachedTagsOfEntitiesEditModal
                  tagEntityType={TagEntityType.Contract}
                  entities={selectedEntities}
                  open={currentAction === BulkActionFlags.UpdateTags}
                  onClose={() => cancelAction()}
                  onSaved={() => {
                    completeAction();
                    paginatedContractsRequest.refetch();
                  }}
                />
                <ContractsDeleteModal
                  entities={EntityHelper.filterEntitiesByIds(
                    paginatedContracts?.items || [],
                    selectedIds as string[],
                  )}
                  open={currentAction === BulkActionFlags.Delete}
                  onClose={() => cancelAction()}
                  onDelete={() => {
                    completeAction();
                    paginatedContractsRequest.refetch();
                  }}
                />
                <DataExportModal
                  open={currentAction === BulkActionFlags.Export}
                  onClose={() => cancelAction()}
                  dataExportProps={{
                    type: ExportType.Contract,
                    currentPageScope: {
                      ids: selectedIds as string[],
                    },
                    onExportDone: completeAction,
                    displayProps: {
                      scope: false,
                    },
                  }}
                />
              </>
            );
          },
        }}
      />

      {/* Export data modal */}
      <DataExportModal
        open={isDataExportModalOpened}
        onClose={() => setIsDataExportModalOpened(false)}
        dataExportProps={{
          type: ExportType.Contract,
          currentPageScope: {
            ids: contractIds,
          },
          allFilteredScope: {
            contract: paginatedContractsRequest.requestParams?.contractGetPaginatedDto,
          },
          onExportDone: () => {
            setIsDataExportModalOpened(false);
          },
        }}
      />
    </ListViewLayout>
  );
}
